import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { PopperProps } from '../Popper.types';

type UseToggleProps = Pick<PopperProps, 'open' | 'disabled' | 'toggleProps' | 'onOpenChange'>;

export const useToggle = ({ open: controlledOpen, disabled, toggleProps, onOpenChange }: UseToggleProps) => {
  const toggleRef = useRef<HTMLElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenPopper = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsOpen(true);

    onOpenChange?.(true);
  }, [disabled, onOpenChange]);

  const handleClosePopper = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsOpen(false);

    onOpenChange?.(false);
  }, [disabled, onOpenChange]);

  const handleToggleClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }

      // Call custom onClick first to allow preventing default toggle behavior
      toggleProps?.onClick?.(e);

      // If the event was stopped, don't toggle the popper state
      if (e.isPropagationStopped()) {
        return;
      }

      setIsOpen(prev => {
        const newValue = !prev;
        onOpenChange?.(newValue);
        return newValue;
      });
    },
    [disabled, toggleProps?.onClick, onOpenChange],
  );

  useEffect(() => {
    if (controlledOpen === undefined || !toggleRef.current) {
      return;
    }

    setIsOpen(controlledOpen);
  }, [controlledOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  return {
    isOpen,
    toggleRef,
    handleOpenPopper,
    handleClosePopper,
    handleToggleClick,
  };
};
